import { useState } from 'react';
import { newId } from '../utils/storage';

// ── 역할 메타데이터 ─────────────────────────────────────
const ROLE_DEFS = {
  'fac:wednesday':       { icon: '🎤', label: '수요 사회자',         bg: '#e0e7ff', color: '#3730a3' },
  'fac:wednesday-noon':  { icon: '🎤', label: '수요 정오 사회자',    bg: '#c7d2fe', color: '#4338ca' },
  'fac:wednesday-eve':   { icon: '🎤', label: '수요 저녁 사회자',    bg: '#ddd6fe', color: '#5b21b6' },
  'fac:sunday':          { icon: '🎤', label: '주일 사회자',         bg: '#fef3c7', color: '#92400e' },
  'pray:wednesday':      { icon: '🙏', label: '수요 기도자',         bg: '#ede9fe', color: '#5b21b6' },
  'pray:wednesday-9am':  { icon: '🙏', label: '수요 정오 기도자',    bg: '#ddd6fe', color: '#6d28d9' },
  'pray:sunday-9am':     { icon: '🙏', label: '주일 9시 기도자',   bg: '#fef9c3', color: '#854d0e' },
  'pray:sunday-noon':    { icon: '🙏', label: '주일 정오 기도자',  bg: '#fce7f3', color: '#9d174d' },
  'usher:always':        { icon: '🚪', label: '안내 (언제나)',       bg: '#d1fae5', color: '#065f46' },
  'usher:wednesday-only':{ icon: '🚪', label: '안내 (수요저녁만)',   bg: '#ede9fe', color: '#4c1d95' },
  'usher:wednesday-noon':{ icon: '🚪', label: '안내 (수요정오만)',   bg: '#a7f3d0', color: '#065f46' },
};

const ROLE_ADD_OPTIONS = [
  { type: 'fac',   sub: 'wednesday',      label: '🎤 수요 사회자 (정오+저녁)' },
  { type: 'fac',   sub: 'wednesday-noon', label: '🎤 수요 정오 사회자 (정오만)' },
  { type: 'fac',   sub: 'wednesday-eve',  label: '🎤 수요 저녁 사회자 (저녁만)' },
  { type: 'fac',   sub: 'sunday',         label: '🎤 주일 사회자' },
  { type: 'pray',  sub: 'wednesday',      label: '🙏 수요 기도자 (정오+저녁)' },
  { type: 'pray',  sub: 'wednesday-9am', label: '🙏 수요 정오 기도자 (정오만)' },
  { type: 'pray',  sub: 'sunday-9am',     label: '🙏 주일 9시 기도자' },
  { type: 'pray',  sub: 'sunday-noon',    label: '🙏 주일 정오 기도자' },
  { type: 'usher', sub: 'always',         label: '🚪 안내 (언제나)' },
  { type: 'usher', sub: 'wednesday-only', label: '🚪 안내 (수요저녁만)' },
  { type: 'usher', sub: 'wednesday-noon', label: '🚪 안내 (수요정오만)' },
];

function roleKey(type, sub) { return `${type}:${sub}`; }

// 한글 초성 추출 (가~힣 → 초성, 나머지는 그대로)
const CHOSUNG = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
function getChosung(char) {
  const code = char.charCodeAt(0) - 0xAC00;
  if (code < 0 || code > 11171) return char;
  return CHOSUNG[Math.floor(code / 588)];
}
function byChosung(a, b) {
  const ca = getChosung(a.name[0] ?? ''), cb = getChosung(b.name[0] ?? '');
  if (ca !== cb) return ca < cb ? -1 : 1;
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
}

// 해당 인원의 역할별 순번 (해당 타입 전체 배정 리스트 내 위치)
function getPosition(assignments, assignmentId, typeKey, subKey) {
  return assignments
    .filter(a => a[typeKey] === subKey)
    .findIndex(a => a.id === assignmentId) + 1;
}

// ── 역할 배지 ───────────────────────────────────────────
function RoleBadge({ def, position, active }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: 6,
      background: active ? def.bg : '#f1f5f9',
      color: active ? def.color : '#94a3b8',
      border: `1px solid ${active ? def.color + '40' : '#e2e8f0'}`,
    }}>
      {def.icon} {def.label}
      <span style={{ opacity: 0.7 }}>#{position}</span>
      {!active && <span style={{ fontSize: '0.65rem' }}>💤</span>}
    </span>
  );
}

// ── 인원 카드 ────────────────────────────────────────────
function PersonCard({ person, facilitators, prayers, ushers, onUpdate, onDelete, onRename }) {
  const [addingRole, setAddingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState('fac:wednesday');
  const [editingName, setEditingName] = useState(false);
  const [editNameVal, setEditNameVal] = useState(person.name);

  const myFacs   = facilitators.filter(f => f.personId === person.id);
  const myPrays  = prayers.filter(p => p.personId === person.id);
  const myUshers = ushers.filter(u => u.personId === person.id);

  const allMyRoles = [
    ...myFacs.map(f => ({ ...f, _type: 'fac',   _sub: f.dayType })),
    ...myPrays.map(p => ({ ...p, _type: 'pray',  _sub: p.dayType })),
    ...myUshers.map(u => ({ ...u, _type: 'usher', _sub: u.usherType })),
  ];

  const existingKeys = new Set(allMyRoles.map(r => roleKey(r._type, r._sub)));
  const addableOptions = ROLE_ADD_OPTIONS.filter(o => !existingKeys.has(roleKey(o.type, o.sub)));

  function toggleRole(role) {
    const next = !role.active;
    if (role._type === 'fac')
      onUpdate({ facilitators: facilitators.map(f => f.id === role.id ? { ...f, active: next } : f) });
    else if (role._type === 'pray')
      onUpdate({ prayers: prayers.map(p => p.id === role.id ? { ...p, active: next } : p) });
    else
      onUpdate({ ushers: ushers.map(u => u.id === role.id ? { ...u, active: next } : u) });
  }

  function removeRole(role) {
    if (role._type === 'fac')
      onUpdate({ facilitators: facilitators.filter(f => f.id !== role.id) });
    else if (role._type === 'pray')
      onUpdate({ prayers: prayers.filter(p => p.id !== role.id) });
    else
      onUpdate({ ushers: ushers.filter(u => u.id !== role.id) });
  }

  function addRole() {
    const [type, sub] = selectedRole.split(':');
    const id = newId();
    if (type === 'fac')
      onUpdate({ facilitators: [...facilitators, { id, personId: person.id, dayType: sub, active: true }] });
    else if (type === 'pray')
      onUpdate({ prayers: [...prayers, { id, personId: person.id, dayType: sub, active: true }] });
    else
      onUpdate({ ushers: [...ushers, { id, personId: person.id, usherType: sub, active: true }] });
    setAddingRole(false);
  }

  const isFullyResting = allMyRoles.length > 0 && allMyRoles.every(r => r.active === false);

  return (
    <div style={{
      background: '#fff', border: `1.5px solid ${isFullyResting ? '#e2e8f0' : 'var(--border)'}`,
      borderRadius: 10, padding: '14px 16px', opacity: isFullyResting ? 0.7 : 1,
    }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
            background: isFullyResting ? '#e2e8f0' : 'var(--primary)',
            color: isFullyResting ? '#94a3b8' : '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: '0.95rem',
          }}>{person.name.charAt(0)}</div>

          {editingName ? (
            <div style={{ display: 'flex', gap: 5, alignItems: 'center', flex: 1 }}>
              <input
                className="form-control"
                style={{ fontSize: '0.95rem', padding: '3px 8px', height: 32, flex: 1, maxWidth: 160 }}
                value={editNameVal}
                autoFocus
                onChange={e => setEditNameVal(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { onRename(person.id, editNameVal); setEditingName(false); }
                  if (e.key === 'Escape') { setEditNameVal(person.name); setEditingName(false); }
                }}
              />
              <button className="btn btn-primary btn-sm" style={{ padding: '2px 10px', fontSize: '0.78rem' }}
                onClick={() => { onRename(person.id, editNameVal); setEditingName(false); }}>저장</button>
              <button className="btn btn-sm" style={{ padding: '2px 8px', fontSize: '0.78rem', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
                onClick={() => { setEditNameVal(person.name); setEditingName(false); }}>취소</button>
            </div>
          ) : (
            <>
              <span
                style={{ fontWeight: 700, fontSize: '1rem', color: isFullyResting ? '#94a3b8' : 'var(--text)', cursor: 'pointer' }}
                title="클릭하여 이름 수정"
                onClick={() => { setEditNameVal(person.name); setEditingName(true); }}
              >
                {person.name}
              </span>
              <button
                className="btn btn-sm"
                style={{ padding: '2px 7px', fontSize: '0.72rem', background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}
                onClick={() => { setEditNameVal(person.name); setEditingName(true); }}
                title="이름 수정"
              >✏️</button>
              {isFullyResting && (
                <span style={{ fontSize: '0.72rem', background: '#f1f5f9', color: '#64748b',
                  padding: '2px 8px', borderRadius: 999, fontWeight: 700 }}>전체 휴식</span>
              )}
            </>
          )}
        </div>
        {!editingName && (
          <button className="btn btn-danger btn-sm" onClick={() => onDelete(person.id)}>삭제</button>
        )}
      </div>

      {/* 역할 목록 */}
      {allMyRoles.length === 0 ? (
        <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: 10 }}>배정된 역할 없음</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {allMyRoles.map(role => {
            const key = roleKey(role._type, role._sub);
            const def = ROLE_DEFS[key];
            if (!def) return null;

            // 순번 계산
            const list   = role._type === 'fac' ? facilitators : role._type === 'pray' ? prayers : ushers;
            const typeKey = role._type === 'usher' ? 'usherType' : 'dayType';
            const pos = getPosition(list, role.id, typeKey, role._sub);

            return (
              <div key={role.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 10px', borderRadius: 7,
                background: role.active !== false ? def.bg + '60' : '#f8fafc',
                border: `1px solid ${role.active !== false ? def.color + '30' : '#e2e8f0'}`,
              }}>
                <RoleBadge def={def} position={pos} active={role.active !== false} />
                <div style={{ display: 'flex', gap: 5 }}>
                  <button
                    className="btn btn-sm"
                    style={{
                      padding: '3px 10px', fontSize: '0.75rem',
                      background: role.active !== false ? '#f1f5f9' : '#dcfce7',
                      color: role.active !== false ? '#475569' : '#166534',
                      border: `1px solid ${role.active !== false ? '#cbd5e1' : '#86efac'}`,
                    }}
                    onClick={() => toggleRole(role)}
                  >
                    {role.active !== false ? '휴식' : '복귀'}
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ padding: '3px 8px', fontSize: '0.75rem',
                      background: '#fff', color: '#94a3b8', border: '1px solid #e2e8f0' }}
                    onClick={() => removeRole(role)}
                    title="역할 제거"
                  >✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 역할 추가 */}
      {addingRole ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
          padding: '8px 10px', background: '#f8fafc', borderRadius: 7, border: '1px dashed #cbd5e1' }}>
          <select className="form-control" style={{ flex: 1, minWidth: 180, fontSize: '0.82rem', padding: '5px 8px' }}
            value={selectedRole}
            onChange={e => setSelectedRole(e.target.value)}>
            {addableOptions.map(o => (
              <option key={roleKey(o.type, o.sub)} value={roleKey(o.type, o.sub)}>{o.label}</option>
            ))}
          </select>
          <button className="btn btn-primary btn-sm" onClick={addRole}>추가</button>
          <button className="btn btn-sm" style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
            onClick={() => setAddingRole(false)}>취소</button>
        </div>
      ) : addableOptions.length > 0 ? (
        <button
          className="btn btn-sm"
          style={{ fontSize: '0.78rem', background: 'transparent', color: 'var(--primary)',
            border: '1px dashed var(--primary)', padding: '4px 12px', borderRadius: 6 }}
          onClick={() => { setSelectedRole(roleKey(addableOptions[0].type, addableOptions[0].sub)); setAddingRole(true); }}
        >
          + 역할 추가
        </button>
      ) : (
        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>모든 역할 배정 완료</span>
      )}
    </div>
  );
}

// ── 역할별 뷰 ───────────────────────────────────────────
const ROLE_GROUPS = [
  { key: 'fac:wednesday',        list: 'facilitators', typeField: 'dayType',    typeVal: 'wednesday' },
  { key: 'fac:wednesday-noon',   list: 'facilitators', typeField: 'dayType',    typeVal: 'wednesday-noon' },
  { key: 'fac:wednesday-eve',    list: 'facilitators', typeField: 'dayType',    typeVal: 'wednesday-eve' },
  { key: 'fac:sunday',           list: 'facilitators', typeField: 'dayType',    typeVal: 'sunday' },
  { key: 'pray:wednesday',       list: 'prayers',      typeField: 'dayType',    typeVal: 'wednesday' },
  { key: 'pray:wednesday-9am',   list: 'prayers',      typeField: 'dayType',    typeVal: 'wednesday-9am' },
  { key: 'pray:sunday-9am',      list: 'prayers',      typeField: 'dayType',    typeVal: 'sunday-9am' },
  { key: 'pray:sunday-noon',     list: 'prayers',      typeField: 'dayType',    typeVal: 'sunday-noon' },
  { key: 'usher:always',         list: 'ushers',       typeField: 'usherType',  typeVal: 'always' },
  { key: 'usher:wednesday-only', list: 'ushers',       typeField: 'usherType',  typeVal: 'wednesday-only' },
  { key: 'usher:wednesday-noon', list: 'ushers',       typeField: 'usherType',  typeVal: 'wednesday-noon' },
];

function RoleGroupView({ facilitators, prayers, ushers, persons, onChange }) {
  const byPersonId = {};
  for (const p of persons) byPersonId[p.id] = p;

  function getList(listName) {
    return listName === 'facilitators' ? facilitators
         : listName === 'prayers'      ? prayers
         : ushers;
  }

  function updateList(listName, next) {
    onChange({
      persons,
      facilitators: listName === 'facilitators' ? next : facilitators,
      prayers:      listName === 'prayers'      ? next : prayers,
      ushers:       listName === 'ushers'       ? next : ushers,
    });
  }

  function toggleActive(listName, id, current) {
    updateList(listName, getList(listName).map(a => a.id === id ? { ...a, active: !current } : a));
  }

  function removeAssignment(listName, id) {
    updateList(listName, getList(listName).filter(a => a.id !== id));
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
      {ROLE_GROUPS.map(group => {
        const def  = ROLE_DEFS[group.key];
        const list = getList(group.list);
        const members = list.filter(a => a[group.typeField] === group.typeVal);
        const active  = members.filter(m => m.active !== false);
        const resting = members.filter(m => m.active === false);

        return (
          <div key={group.key} style={{
            background: '#fff', border: '1.5px solid var(--border)', borderRadius: 10, padding: '14px 16px',
          }}>
            {/* 그룹 헤더 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: '1.1rem' }}>{def.icon}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: def.color }}>{def.label}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  활동 {active.length}명{resting.length > 0 ? ` / 휴식 ${resting.length}명` : ''}
                </div>
              </div>
            </div>

            {/* 활동 중 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: resting.length ? 8 : 0 }}>
              {active.map((a, i) => {
                const person = byPersonId[a.personId];
                if (!person) return null;
                return (
                  <div key={a.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 10px', background: def.bg + '70', borderRadius: 7,
                    border: `1px solid ${def.color}30`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: def.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                        {i + 1}
                      </div>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{person.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm"
                        style={{ padding: '2px 9px', fontSize: '0.73rem', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
                        onClick={() => toggleActive(group.list, a.id, true)}>휴식</button>
                      <button className="btn btn-sm"
                        style={{ padding: '2px 7px', fontSize: '0.73rem', background: '#fff', color: '#94a3b8', border: '1px solid #e2e8f0' }}
                        onClick={() => removeAssignment(group.list, a.id)}>✕</button>
                    </div>
                  </div>
                );
              })}
              {active.length === 0 && (
                <div style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '4px 0' }}>활동 중인 인원 없음</div>
              )}
            </div>

            {/* 휴식 중 */}
            {resting.length > 0 && (
              <div style={{ borderRadius: 7, background: '#f8fafc', border: '1.5px dashed #cbd5e1', padding: '7px 10px' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', marginBottom: 5 }}>
                  💤 휴식 중
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {resting.map(a => {
                    const person = byPersonId[a.personId];
                    if (!person) return null;
                    return (
                      <div key={a.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '5px 8px', background: '#fff', borderRadius: 6, border: '1px solid #e2e8f0',
                      }}>
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 600 }}>{person.name}</span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-success btn-sm"
                            style={{ padding: '2px 9px', fontSize: '0.73rem' }}
                            onClick={() => toggleActive(group.list, a.id, false)}>복귀</button>
                          <button className="btn btn-sm"
                            style={{ padding: '2px 7px', fontSize: '0.73rem', background: '#fff', color: '#94a3b8', border: '1px solid #e2e8f0' }}
                            onClick={() => removeAssignment(group.list, a.id)}>✕</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── 메인 ────────────────────────────────────────────────
export default function MemberManager({ persons, facilitators, prayers, ushers, onChange }) {
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('none'); // 'none' | roleKey
  const [viewMode, setViewMode] = useState('role'); // 'role' | 'person'
  const [sorted, setSorted] = useState(false);
  const [filterChosung, setFilterChosung] = useState(null); // null = 전체

  // 목록에 실제 존재하는 초성만 추출 (가나다순)
  const presentChosung = [...new Set(persons.map(p => getChosung(p.name[0] ?? '')))].sort();

  const displayPersons = (() => {
    let list = sorted ? [...persons].sort(byChosung) : persons;
    if (filterChosung) list = list.filter(p => getChosung(p.name[0] ?? '') === filterChosung);
    return list;
  })();

  function addPerson() {
    const name = newName.trim();
    if (!name) return;
    if (persons.some(p => p.name === name)) { alert('이미 같은 이름의 인원이 있습니다.'); return; }
    const personId = newId();
    let nextFacs  = facilitators;
    let nextPrays = prayers;
    let nextUshers = ushers;
    if (newRole !== 'none') {
      const [type, sub] = newRole.split(':');
      const aid = newId();
      if (type === 'fac')   nextFacs   = [...facilitators, { id: aid, personId, dayType: sub, active: true }];
      else if (type === 'pray') nextPrays  = [...prayers,  { id: aid, personId, dayType: sub, active: true }];
      else                  nextUshers = [...ushers,       { id: aid, personId, usherType: sub, active: true }];
    }
    onChange({ persons: [...persons, { id: personId, name }], facilitators: nextFacs, prayers: nextPrays, ushers: nextUshers });
    setNewName('');
    setNewRole('none');
  }

  function deletePerson(personId) {
    onChange({
      persons: persons.filter(p => p.id !== personId),
      facilitators: facilitators.filter(f => f.personId !== personId),
      prayers: prayers.filter(p => p.personId !== personId),
      ushers: ushers.filter(u => u.personId !== personId),
    });
  }

  function renamePerson(personId, newNameVal) {
    const trimmed = newNameVal.trim();
    if (!trimmed) return;
    if (persons.some(p => p.id !== personId && p.name === trimmed)) {
      alert('이미 같은 이름의 인원이 있습니다.'); return;
    }
    onChange({ persons: persons.map(p => p.id === personId ? { ...p, name: trimmed } : p), facilitators, prayers, ushers });
  }

  function handleRoleUpdate(personId, patch) {
    onChange({
      persons,
      facilitators: patch.facilitators ?? facilitators,
      prayers:      patch.prayers      ?? prayers,
      ushers:       patch.ushers       ?? ushers,
    });
  }

  const activeCount = persons.filter(p => {
    const roles = [
      ...facilitators.filter(f => f.personId === p.id),
      ...prayers.filter(pr => pr.personId === p.id),
      ...ushers.filter(u => u.personId === p.id),
    ];
    return roles.some(r => r.active !== false);
  }).length;

  return (
    <div>
      <div className="alert alert-info">
        📌 한 사람이 여러 역할을 가질 수 있습니다. 사회자로 배정된 날은 동일 인물이 안내위원에서 자동 제외됩니다.
        명단 수정 후 <strong>💾 저장 &amp; 재배정</strong>을 눌러야 스케줄에 반영됩니다.
      </div>

      {/* 헤더: 인원 추가 + 뷰 토글 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div className="card-title" style={{ marginBottom: 10 }}>
              👤 인원 관리
              <span className="badge">{persons.length}명 등록</span>
              <span className="badge" style={{ background: 'var(--success)' }}>{activeCount}명 활동</span>
            </div>
            <div className="form-row" style={{ marginBottom: 0, alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>이름</label>
                <input className="form-control" placeholder="새 인원 이름" value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addPerson()} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>역할 (선택)</label>
                <select className="form-control" value={newRole} onChange={e => setNewRole(e.target.value)}>
                  <option value="none">역할 없음</option>
                  {ROLE_ADD_OPTIONS.map(o => (
                    <option key={roleKey(o.type, o.sub)} value={roleKey(o.type, o.sub)}>{o.label}</option>
                  ))}
                </select>
              </div>
              <button className="btn btn-primary btn-sm" style={{ height: 38 }} onClick={addPerson}>+ 인원 추가</button>
            </div>
          </div>

          {/* 뷰 토글 + 정렬 */}
          <div style={{ alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <button
            onClick={() => setSorted(s => !s)}
            style={{
              padding: '5px 14px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', borderRadius: 6,
              background: sorted ? 'var(--primary)' : '#fff',
              color: sorted ? '#fff' : 'var(--primary)',
              border: '2px solid var(--primary)',
              transition: 'background 0.15s, color 0.15s',
            }}
          >{sorted ? '🔤 가나다순 ✓' : '🔤 가나다순'}</button>
          <div style={{ display: 'flex', gap: 0, border: '2px solid var(--primary)', borderRadius: 8, overflow: 'hidden' }}>
            <button
              onClick={() => setViewMode('role')}
              style={{
                padding: '8px 18px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer', border: 'none',
                background: viewMode === 'role' ? 'var(--primary)' : '#fff',
                color: viewMode === 'role' ? '#fff' : 'var(--primary)',
                transition: 'background 0.15s, color 0.15s',
              }}
            >📋 역할별 보기</button>
            <button
              onClick={() => setViewMode('person')}
              style={{
                padding: '8px 18px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
                borderLeft: '2px solid var(--primary)', borderTop: 'none', borderRight: 'none', borderBottom: 'none',
                background: viewMode === 'person' ? 'var(--primary)' : '#fff',
                color: viewMode === 'person' ? '#fff' : 'var(--primary)',
                transition: 'background 0.15s, color 0.15s',
              }}
            >👤 인원별 보기</button>
          </div>
          </div>
        </div>

        {/* 초성 필터 */}
        {persons.length > 0 && (
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginRight: 2 }}>초성</span>
            <button
              onClick={() => setFilterChosung(null)}
              style={{
                padding: '3px 12px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', borderRadius: 6,
                border: '1.5px solid var(--border)',
                background: filterChosung === null ? 'var(--primary)' : '#fff',
                color: filterChosung === null ? '#fff' : 'var(--text-muted)',
              }}
            >전체</button>
            {presentChosung.map(cho => (
              <button
                key={cho}
                onClick={() => setFilterChosung(c => c === cho ? null : cho)}
                style={{
                  width: 36, height: 36, fontSize: '0.9rem', fontWeight: 800, cursor: 'pointer', borderRadius: 6,
                  border: '1.5px solid',
                  borderColor: filterChosung === cho ? 'var(--primary)' : 'var(--border)',
                  background: filterChosung === cho ? 'var(--primary)' : '#fff',
                  color: filterChosung === cho ? '#fff' : 'var(--text)',
                  transition: 'all 0.12s',
                }}
              >{cho}</button>
            ))}
            {filterChosung && (
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginLeft: 4 }}>
                {displayPersons.length}명
              </span>
            )}
          </div>
        )}
      </div>

      {/* 역할별 뷰 */}
      {viewMode === 'role' && (
        persons.length === 0
          ? <div className="card"><div className="empty-state"><div className="empty-icon">👥</div><div>인원을 추가해주세요</div></div></div>
          : <RoleGroupView
              facilitators={facilitators}
              prayers={prayers}
              ushers={ushers}
              persons={displayPersons}
              onChange={onChange}
            />
      )}

      {/* 인원별 뷰 */}
      {viewMode === 'person' && (
        persons.length === 0
          ? <div className="card"><div className="empty-state"><div className="empty-icon">👥</div><div>인원을 추가해주세요</div></div></div>
          : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
              {displayPersons.map(p => (
                <PersonCard
                  key={p.id}
                  person={p}
                  facilitators={facilitators}
                  prayers={prayers}
                  ushers={ushers}
                  onUpdate={patch => handleRoleUpdate(p.id, patch)}
                  onDelete={deletePerson}
                  onRename={renamePerson}
                />
              ))}
            </div>
      )}
    </div>
  );
}
